import { c as _c } from "react/compiler-runtime";
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { DISABLE_MAIN_AND_RELEASE_BRANCH_EDITING_FLAG_KEY } from '@/services/flags/featureFlagDefinitions';
import { NetworkStatus } from '@/services/models/enums';
import { Network, useNetwork } from './useNetwork';
export type NetworkEditability = {
  isReadOnly: boolean;
  // A frozen released network — read-only regardless of any flag.
  isReleased: boolean;
  // KBE-558: a merge baseline. Hidden from every list, but reachable by URL,
  // so the page still needs to explain why nothing can be edited.
  isSnapshot: boolean;
  // KBE-749: the master network / a release branch while the editing-lock flag
  // is on. Distinguished from `isReleased` so the UI can explain why.
  isBranchLocked: boolean;
};
export function deriveNetworkEditability(network: Pick<Network, 'status' | 'isMain' | 'isReleaseBranch'> | undefined, mainAndReleaseEditingLocked: boolean): NetworkEditability {
  const isReleased = network?.status === NetworkStatus.RELEASED;
  const isSnapshot = network?.status === NetworkStatus.SNAPSHOT;
  const isBranchLocked = mainAndReleaseEditingLocked && (network?.isMain === true || network?.isReleaseBranch === true);
  return {
    isReleased,
    isSnapshot,
    isBranchLocked,
    isReadOnly: isReleased || isSnapshot || isBranchLocked
  };
}

/** Whether the current network can be edited in the UI, and why not. Mirrors the
 * server guard in `assertNetworkWritable`: released networks and snapshots are
 * always read-only, and (KBE-749) the master network + release branches are
 * read-only while the `disable_main_and_release_branch_editing` flag is on. */
export function useNetworkEditable(networkId) {
  const $ = _c(5);
  let t0;
  if ($[0] !== networkId) {
    t0 = {
      networkId
    };
    $[0] = networkId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    network
  } = useNetwork(t0);
  const mainAndReleaseEditingLocked = useFeatureFlag(DISABLE_MAIN_AND_RELEASE_BRANCH_EDITING_FLAG_KEY);
  const current = network !== undefined && network.id === networkId ? network : undefined;
  let t1;
  if ($[2] !== current || $[3] !== mainAndReleaseEditingLocked) {
    t1 = deriveNetworkEditability(current, mainAndReleaseEditingLocked);
    $[2] = current;
    $[3] = mainAndReleaseEditingLocked;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}